import { isValid } from 'date-fns';
import { DateType } from 'ui/datePicker';
import { DATE_FORMAT_FULL, displayDate, getNewDate } from 'utils/date';
import { localizeDateRange, localizeDateTime } from 'utils/l10n';

/**
 * Time Zone is enforced as PDT, and consequently unchangeable in the date
 * picker, because the data aggregation happens on a schedule in PDT.
 */

export const PST_DATETIME_SUFFIX = 'T08:00:00.000Z';

export const convertDateToPST = (date: DateType) =>
  date?.toISOString(true).replace(/T(.*)/g, PST_DATETIME_SUFFIX);

export const getDaysAgoFromToday = (days: number) => getNewDate().subtract(days, 'day');

export const thirtyDaysAgo = () => getDaysAgoFromToday(30);

export const fourteenDaysAgo = () => getDaysAgoFromToday(14);

export const sevenDaysAgo = () => getDaysAgoFromToday(7);

export const yesterday = () => getDaysAgoFromToday(1);

export const yesterdayDisplay = () => displayDate(yesterday(), DATE_FORMAT_FULL);

/**
 * Get the earliest date from a list of date strings, some of which could be undefined
 */
export const getMinDate = (dates: (string | undefined)[]): string =>
  dates.reduce((acc: string, curr) => {
    /** if a date string is stored, compare to new value */
    if (!!acc && !!curr) {
      return Date.parse(acc) < Date.parse(curr) ? acc : curr;
    }
    /** if current date is falsy and the stored date is falsy, return stored value and move on to the next array item */
    return curr || acc;
  }, '');

/** Create a single localized date for display*/
export const createAsOfDateText = (endDate: string) => {
  const date = new Date(endDate.concat(PST_DATETIME_SUFFIX));
  return isValid(date) ? localizeDateTime(date) : '';
};

/** Create a localized date range for display */
export const createDateRangeText = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) {
    return '';
  }
  const isBefore = Date.parse(endDate) < Date.parse(startDate);
  const { range } = localizeDateRange(
    new Date(startDate.concat(PST_DATETIME_SUFFIX)),
    new Date(endDate.concat(PST_DATETIME_SUFFIX)),
  );
  return isBefore ? `As of ${createAsOfDateText(endDate)}` : range || '';
};
